import { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  alpha,
} from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import { getWifiUsersDashboard } from "../services/api";
import { FiRefreshCw, FiTrendingUp, FiUsers, FiDatabase } from "react-icons/fi";
import { GrAnalytics } from "react-icons/gr";
import { MdOutlineTableChart, MdBarChart } from "react-icons/md";

export default function WifiUserDashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewType, setViewType] = useState("table");
  const [sortBy, setSortBy] = useState("users");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    fetchData();
  }, [sortBy, sortOrder]);

  const getBarColor = (value) => {
    if (value > 100) return "#ef4444";
    if (value > 75) return "#f59e0b";
    if (value > 50) return "#3b82f6";
    return "#10b981";
  };

  const getUsageLevel = (users) => {
    if (users > 100) return { label: "Critical", color: "#ef4444", bg: "#fee2e2" };
    if (users > 75) return { label: "High", color: "#f59e0b", bg: "#fef3c7" };
    if (users > 50) return { label: "Medium", color: "#3b82f6", bg: "#dbeafe" };
    return { label: "Low", color: "#10b981", bg: "#d1fae5" };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getWifiUsersDashboard(sortBy, sortOrder);
      if (response.success) {
        setData(response.data);
      } else {
        setError("Failed to fetch dashboard data");
      }
    } catch (err) {
      setError(err.message || "An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  };

  const totalUsers = data.reduce((sum, item) => sum + item.activeUsers, 0);
  const highUsageDepts = data.filter(d => d.activeUsers > 100).length;
  const avgUsers = data.length > 0 ? Math.round(totalUsers / data.length) : 0;

  return (
    <Box sx={{ 
      minHeight: "100vh", 
      // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      py: 4 
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: 3,
              boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 2,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <GrAnalytics style={{ fontSize: "28px", color: "white" }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a237e", mb: 0.5 }}>
                    WiFi Users Dashboard
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Real-time monitoring of departmental WiFi usage
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`Last updated: ${new Date().toLocaleTimeString()}`}
                variant="outlined"
                sx={{ borderColor: "#667eea", color: "#667eea" }}
              />
            </Box>
          </Paper>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 3, mb: 4 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 20px 40px rgba(102, 126, 234, 0.3)",
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <FiDatabase size={32} style={{ opacity: 0.9 }} />
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {data.length}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                Total Departments
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Active departments in system
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              color: "white",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 20px 40px rgba(245, 87, 108, 0.3)",
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <FiUsers size={32} style={{ opacity: 0.9 }} />
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {totalUsers.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                Total Active Users
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Across all departments
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              color: "white",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 20px 40px rgba(79, 172, 254, 0.3)",
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <FiTrendingUp size={32} style={{ opacity: 0.9 }} />
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {highUsageDepts}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                High Usage Departments
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Exceeding 100 users
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
              color: "white",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 20px 40px rgba(250, 112, 154, 0.3)",
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <GrAnalytics size={32} style={{ opacity: 0.9 }} />
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {avgUsers}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                Average Users/Dept
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Department average
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Controls */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <FormControl fullWidth>
                <InputLabel>View Type</InputLabel>
                <Select 
                  value={viewType} 
                  onChange={(e) => setViewType(e.target.value)} 
                  label="View Type"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="table">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <MdOutlineTableChart /> Table View
                    </Box>
                  </MenuItem>
                  <MenuItem value="chart">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <MdBarChart /> Chart View
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: 1, minWidth: 180 }}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sort By" sx={{ borderRadius: 2 }}>
                  <MenuItem value="users">Active Users</MenuItem>
                  <MenuItem value="department">Department Name</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: 1, minWidth: 180 }}>
              <FormControl fullWidth>
                <InputLabel>Sort Order</InputLabel>
                <Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} label="Sort Order" sx={{ borderRadius: 2 }}>
                  <MenuItem value="desc">Descending (High to Low)</MenuItem>
                  <MenuItem value="asc">Ascending (Low to High)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Button
              variant="contained"
              onClick={fetchData}
              disabled={loading}
              startIcon={<FiRefreshCw style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 8px 20px rgba(102, 126, 234, 0.4)",
                },
              }}
            >
              {loading ? "Loading..." : "Refresh Data"}
            </Button>
          </Box>
        </Paper>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#667eea" }} />
          </Box>
        )}

        {!loading && viewType === "table" && (
          <TableContainer 
            component={Paper}
            elevation={0}
            sx={{ 
              borderRadius: 3,
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)" }}>
                  <TableCell sx={{ fontWeight: 700, color: "white", fontSize: "1rem", py: 2 }}>
                    Department Name
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "white", fontSize: "1rem", py: 2 }}>
                    Active Users
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "white", fontSize: "1rem", py: 2 }}>
                    Usage Level
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => {
                  const usage = getUsageLevel(row.activeUsers);
                  return (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: alpha(usage.color, 0.1),
                          transform: "scale(1.01)",
                        },
                        cursor: "pointer",
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: "#1a237e", py: 1.5 }}>
                        {row.departmentName}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: usage.color, fontSize: "1.1rem" }}>
                        {row.activeUsers.toLocaleString()}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={usage.label}
                          size="small"
                          sx={{
                            backgroundColor: usage.bg,
                            color: usage.color,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && viewType === "chart" && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.95)",
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a237e", mb: 1 }}>
                Active Users by Department
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                {data.length} departments • Total: {totalUsers.toLocaleString()} active users • Average: {avgUsers} per department
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={600}>
              <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 120 }}>
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#667eea" stopOpacity={0.9}/>
                    <stop offset="100%" stopColor="#764ba2" stopOpacity={0.7}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="departmentName"
                  angle={-45}
                  textAnchor="end"
                  height={120}
                  interval={0}
                  tick={{ fontSize: 12, fill: "#475569", fontWeight: 500 }}
                />
                <YAxis
                  label={{ value: "Active Users", angle: -90, position: "insideLeft", style: { fill: "#475569", fontWeight: 600 } }}
                  tick={{ fill: "#475569", fontWeight: 500 }}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "none",
                    borderRadius: 8,
                    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                    padding: "12px",
                  }}
                  labelStyle={{ fontWeight: 700, color: "#1a237e" }}
                />
                <Legend wrapperStyle={{ paddingTop: "20px" }} />
                <Bar dataKey="activeUsers" name="Active Users" radius={[8, 8, 0, 0]}>
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getBarColor(entry.activeUsers)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            
            {/* Legend */}
            <Box sx={{ mt: 4, display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center", p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
              {[
                { color: "#ef4444", label: "Critical (>100 users)" },
                { color: "#f59e0b", label: "High (76-100 users)" },
                { color: "#3b82f6", label: "Medium (51-75 users)" },
                { color: "#10b981", label: "Low (≤50 users)" },
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, backgroundColor: item.color, borderRadius: 1 }} />
                  <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {!loading && data.length === 0 && (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No data available. Please check back later.
          </Alert>
        )}
      </Container>
    </Box>
  );
}